import { useState } from 'react';
import {
  useStore, Panel, Badge, Empty, Modal, Field, Flash, useFlash, plural, useCan,
} from '../components/ui.jsx';
import { savePlan, setPlanActive, saveBookingPolicy } from '../data/store.js';
import { format } from '../lib/money.js';
import { PLAN_KINDS } from '../lib/entitlements.js';

const KIND_LABEL = {
  [PLAN_KINDS.RECURRING_UNLIMITED]: 'Recurring · unlimited',
  [PLAN_KINDS.RECURRING_CAPPED]: 'Recurring · capped',
  [PLAN_KINDS.PACK]: 'Credit pack',
  [PLAN_KINDS.DROP_IN]: 'Drop-in',
};

const isCredit = (kind) => [PLAN_KINDS.PACK, PLAN_KINDS.DROP_IN].includes(kind);
const isRecurring = (kind) => [
  PLAN_KINDS.RECURRING_UNLIMITED, PLAN_KINDS.RECURRING_CAPPED,
].includes(kind);

/** Cents from a text field, without letting a stray character become NaN. */
const toCents = (text) => Math.round((Number(String(text).replace(/[^0-9.]/g, '')) || 0) * 100);
const toMajor = (cents) => ((cents ?? 0) / 100).toFixed(2);

export default function Plans() {
  const state = useStore();
  const can = useCan();
  const [editing, setEditing] = useState(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [flash, setFlash] = useFlash();

  const mayEdit = can('plans.write');
  const mayEditPolicy = can('policy.write');

  const counts = state.memberships.reduce((acc, m) => {
    if (m.status !== 'active') return acc;
    acc[m.planId] = (acc[m.planId] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <header className="topbar">
        <h1>Plans</h1>
        <span className="sub">{plural(state.plans.length, 'plan', 'plans')} configured</span>
        <div className="spacer" />
        {mayEdit && (
          <button className="primary" onClick={() => setEditing({})}>New plan</button>
        )}
      </header>

      <div className="page">
        <Flash flash={flash} />

        {!mayEdit && (
          <div className="notice info">
            Plans and the booking policy decide what the gym sells, so only an
            owner or admin can change them.
          </div>
        )}

        <Panel bodyless>
          {state.plans.length === 0 ? <Empty>No plans yet.</Empty> : (
            <table className="data">
              <thead>
                <tr>
                  <th>Plan</th><th>Type</th><th>Entitlement</th><th>Programs</th>
                  <th className="right">Members</th><th className="right">Price</th>
                  {mayEdit && <th className="right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {state.plans.map((p) => (
                  <tr key={p.id} className={p.active ? undefined : 'is-cancelled'}>
                    <td>
                      <b>{p.name}</b>
                      {!p.active && <> <Badge tone="neutral">retired</Badge></>}
                    </td>
                    <td style={{ color: 'var(--ink-2)' }}>{KIND_LABEL[p.kind] ?? p.kind}</td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                      {p.kind === PLAN_KINDS.RECURRING_CAPPED && `${p.limit} per ${p.limitPeriod}`}
                      {p.kind === PLAN_KINDS.RECURRING_UNLIMITED && 'unlimited'}
                      {isCredit(p.kind) && `${plural(p.credits, 'credit', 'credits')} · ${p.expiryDays}d expiry`}
                    </td>
                    <td>
                      {p.programs?.length
                        ? p.programs.map((x) => <Badge key={x} tone="accent">{x}</Badge>)
                        : <span style={{ color: 'var(--ink-3)' }}>all</span>}
                    </td>
                    <td className="right mono num">{counts[p.id] || 0}</td>
                    <td className="right mono num">
                      {format(p.amountCents)}
                      {p.interval && <span style={{ color: 'var(--ink-3)' }}>/{p.interval[0]}</span>}
                    </td>
                    {mayEdit && (
                      <td className="right">
                        <div className="row" style={{ justifyContent: 'flex-end' }}>
                          <button className="small" onClick={() => setEditing(p)}>Edit</button>
                          <button
                            className="small"
                            onClick={() => {
                              const r = setPlanActive(p.id, !p.active);
                              setFlash({
                                tone: 'good',
                                message: p.active
                                  ? `${p.name} retired. ${plural(r.holders, 'member', 'members')} already on it are unaffected.`
                                  : `${p.name} is on sale again.`,
                              });
                            }}
                          >
                            {p.active ? 'Retire' : 'Restore'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel
          title="Booking policy"
          actions={mayEditPolicy && (
            <button className="small" onClick={() => setPolicyOpen(true)}>Edit</button>
          )}
        >
          <dl className="kv">
            <dt>Booking opens</dt><dd className="num">{Math.round(state.policy.opensHoursBefore / 24)} days before</dd>
            <dt>Booking closes</dt><dd className="num">{state.policy.closesMinutesBefore} min before start</dd>
            <dt>Waitlist</dt><dd>{state.policy.waitlist ? `On, capped at ${state.policy.waitlistCap}` : 'Off'}</dd>
            <dt>Late cancel</dt><dd className="num">within {Math.round(state.policy.lateCancelMinutes / 60)}h · {format(state.policy.lateCancelFeeCents)} fee</dd>
            <dt>No-show fee</dt><dd className="num">{format(state.policy.noShowFeeCents)}</dd>
            <dt>Late cancel credit</dt><dd>{state.policy.lateCancelReturnsCredit ? 'Returned' : 'Forfeited'}</dd>
          </dl>
        </Panel>
      </div>

      {editing && (
        <EditPlan
          plan={editing}
          onClose={() => setEditing(null)}
          onSaved={(r) => {
            setEditing(null);
            setFlash({
              tone: 'good',
              message: r.created ? `${r.plan.name} created.` : `${r.plan.name} updated.`,
            });
          }}
        />
      )}

      {policyOpen && (
        <EditPolicy
          policy={state.policy}
          onClose={() => setPolicyOpen(false)}
          onSaved={() => { setPolicyOpen(false); setFlash({ tone: 'good', message: 'Booking policy updated.' }); }}
        />
      )}
    </>
  );
}

function EditPlan({ plan, onClose, onSaved }) {
  const [form, setForm] = useState({
    kind: plan.kind ?? PLAN_KINDS.RECURRING_UNLIMITED,
    name: plan.name ?? '',
    price: toMajor(plan.amountCents),
    interval: plan.interval ?? 'month',
    limit: plan.limit ?? 3,
    limitPeriod: plan.limitPeriod ?? 'week',
    credits: plan.credits ?? 10,
    expiryDays: plan.expiryDays ?? 90,
    programs: (plan.programs || []).join(', '),
    blockWhenPastDue: plan.blockWhenPastDue ?? false,
  });
  const [errors, setErrors] = useState([]);
  const set = (k) => (e) => setForm({
    ...form,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  });

  function save() {
    const next = {
      ...plan,
      name: form.name.trim(),
      kind: form.kind,
      amountCents: toCents(form.price),
      programs: form.programs.split(',').map((s) => s.trim()).filter(Boolean),
      blockWhenPastDue: form.blockWhenPastDue,
      // Only carry the fields this kind of plan actually uses. A stale limit
      // left on a pack is how a plan ends up meaning two things at once.
      interval: isRecurring(form.kind) ? form.interval : null,
      limit: form.kind === PLAN_KINDS.RECURRING_CAPPED ? Number(form.limit) : null,
      limitPeriod: form.kind === PLAN_KINDS.RECURRING_CAPPED ? form.limitPeriod : null,
      credits: isCredit(form.kind) ? Number(form.credits) : null,
      expiryDays: isCredit(form.kind) ? Number(form.expiryDays) : null,
    };
    const r = savePlan(next);
    if (!r.ok) return setErrors(r.errors);
    return onSaved(r);
  }

  return (
    <Modal
      title={plan.id ? `Edit ${plan.name}` : 'New plan'}
      onClose={onClose}
      footer={<button className="primary" onClick={save}>{plan.id ? 'Save' : 'Create plan'}</button>}
    >
      <div className="stack">
        {errors.length > 0 && (
          <div className="notice bad">
            {errors.map((e) => <div key={e}>{e}</div>)}
          </div>
        )}

        <div className="form-grid">
          <Field label="Name"><input value={form.name} onChange={set('name')} autoFocus /></Field>
          <Field label="Type">
            <select value={form.kind} onChange={set('kind')}>
              {Object.entries(KIND_LABEL).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </Field>
          <Field label="Price"><input value={form.price} onChange={set('price')} inputMode="decimal" /></Field>

          {isRecurring(form.kind) && (
            <Field label="Bills every">
              <select value={form.interval} onChange={set('interval')}>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </Field>
          )}

          {form.kind === PLAN_KINDS.RECURRING_CAPPED && (
            <>
              <Field label="Classes allowed">
                <input type="number" min="1" value={form.limit} onChange={set('limit')} />
              </Field>
              <Field label="Per">
                <select value={form.limitPeriod} onChange={set('limitPeriod')}>
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="cycle">Billing cycle</option>
                </select>
              </Field>
            </>
          )}

          {isCredit(form.kind) && (
            <>
              <Field label="Credits">
                <input type="number" min="1" value={form.credits} onChange={set('credits')} />
              </Field>
              <Field label="Expire after (days)">
                <input type="number" min="1" value={form.expiryDays} onChange={set('expiryDays')} />
              </Field>
            </>
          )}

          <Field label="Programs (blank for all)">
            <input value={form.programs} onChange={set('programs')} placeholder="yoga, crossfit" />
          </Field>
        </div>

        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.blockWhenPastDue}
            onChange={set('blockWhenPastDue')}
            style={{ width: 'auto' }}
          />
          <span>Refuse entry while the account is past due</span>
        </label>
        <p className="hint" style={{ margin: 0 }}>
          Boutique studios usually let a member in and chase the card. A 24/7
          gym locks the door. Leave this off unless you mean it.
        </p>
      </div>
    </Modal>
  );
}

function EditPolicy({ policy, onClose, onSaved }) {
  const [form, setForm] = useState({
    opensDays: Math.round(policy.opensHoursBefore / 24),
    closesMinutesBefore: policy.closesMinutesBefore,
    waitlist: policy.waitlist,
    waitlistCap: policy.waitlistCap,
    lateCancelHours: Math.round(policy.lateCancelMinutes / 60),
    lateCancelFee: toMajor(policy.lateCancelFeeCents),
    noShowFee: toMajor(policy.noShowFeeCents),
    lateCancelReturnsCredit: policy.lateCancelReturnsCredit,
  });
  const [errors, setErrors] = useState([]);
  const set = (k) => (e) => setForm({
    ...form,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  });

  function save() {
    const r = saveBookingPolicy({
      opensHoursBefore: Number(form.opensDays) * 24,
      closesMinutesBefore: Number(form.closesMinutesBefore),
      waitlist: form.waitlist,
      waitlistCap: Number(form.waitlistCap),
      lateCancelMinutes: Number(form.lateCancelHours) * 60,
      lateCancelFeeCents: toCents(form.lateCancelFee),
      noShowFeeCents: toCents(form.noShowFee),
      lateCancelReturnsCredit: form.lateCancelReturnsCredit,
    });
    if (!r.ok) return setErrors(r.errors);
    return onSaved(r);
  }

  return (
    <Modal
      title="Booking policy"
      onClose={onClose}
      footer={<button className="primary" onClick={save}>Save policy</button>}
    >
      <div className="stack">
        {errors.length > 0 && (
          <div className="notice bad">{errors.map((e) => <div key={e}>{e}</div>)}</div>
        )}

        <div className="form-grid">
          <Field label="Booking opens (days before)">
            <input type="number" min="0" value={form.opensDays} onChange={set('opensDays')} />
          </Field>
          <Field label="Booking closes (minutes before)">
            <input type="number" min="0" value={form.closesMinutesBefore} onChange={set('closesMinutesBefore')} />
          </Field>
          <Field label="Late cancel window (hours)">
            <input type="number" min="0" value={form.lateCancelHours} onChange={set('lateCancelHours')} />
          </Field>
          <Field label="Late cancel fee">
            <input value={form.lateCancelFee} onChange={set('lateCancelFee')} inputMode="decimal" />
          </Field>
          <Field label="No-show fee">
            <input value={form.noShowFee} onChange={set('noShowFee')} inputMode="decimal" />
          </Field>
          {form.waitlist && (
            <Field label="Waitlist cap">
              <input type="number" min="1" value={form.waitlistCap} onChange={set('waitlistCap')} />
            </Field>
          )}
        </div>

        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.waitlist} onChange={set('waitlist')} style={{ width: 'auto' }} />
          <span>Keep a waitlist when a class is full</span>
        </label>

        <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.lateCancelReturnsCredit}
            onChange={set('lateCancelReturnsCredit')}
            style={{ width: 'auto' }}
          />
          <span>Return the credit on a late cancel</span>
        </label>
        <p className="hint" style={{ margin: 0 }}>
          These apply to every class immediately. Members already booked keep
          their seats; the rules change for what happens next.
        </p>
      </div>
    </Modal>
  );
}
